import { useCallback, useState } from 'react';

import { EMAIL_SENDER, SKIP_CORPORATE_EMAIL_STEP } from '../constantsConfig/constantsConfig';
import { apiService } from '../services';
import type {
  CpfFormSnapshot,
  FinalizeRepresentativeArgs,
  PolicyVinculoCheckResult,
  UsePolicyholderReturn,
} from '../types/hookType';
import { isRepresentativeUserRoleCode } from '../types/registerPolicyholderType';
import { isPersonalEmailDomain, pickFirstNonPersonalEmail } from '../utils/emailCompanyRule';
import {
  buildPolicyholderWelcomeEmailHtml,
  POLICYHOLDER_WELCOME_EMAIL_SUBJECT,
} from '../utils/emailUtils';
import { generateRandomPassword8 } from '../utils/randomPasswordUtils';
import {
  buildEstpChaveLegado,
  policyDateToApiIso,
  selectedTypeToOrigemDadoId,
  selectedTypeToRamoDigit,
} from '../utils/chaveLegadoUtils';
import { selectedTypeToProductId } from '../utils/registerPolicyholderUtil';
import {
  ORIGEM_DADO_ADICIONAR_EMAIL,
  TipoPessoaCadp,
  type AddBasicPhysicalPersonDataRequest,
  type BusinessLinkItem,
  type CnpjData,
  type CpfData,
  type UserPoliciesListItem,
} from '../types/serviceType';

function digitsOnly(s: string): string {
  return s.replace(/\D/g, '');
}

/** CPF digits or CNPJ alphanumeric (keeps letters). */
function documentAlnum(s: string): string {
  return s.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
}

/**
 * Document segment for ESTP legacy key: CPF (11 digits) or CNPJ (14 alphanumeric).
 * Returns null when the value cannot be used in `buildEstpChaveLegado`.
 */
function documentoParaChaveLegado(s: string): string | null {
  const alnum = documentAlnum(s);
  if (/^\d{11}$/.test(alnum) || alnum.length === 14) {
    return alnum;
  }
  return null;
}

/** CADP `responsavelInclusao` accepts at most 8 numeric digits. */
function toResponsavelInclusao8(id: number): number {
  const digits = String(Math.trunc(Math.abs(id))).replace(/\D/g, '');
  if (!digits) return 0;
  return Number(digits.slice(-8));
}

function isValidEmail(value: string | null | undefined): value is string {
  if (!value) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function firstContactFromList(
  contatos: Array<{ email: string | null; telefone: string | null }>
): { email: string; phone: string } {
  const first = contatos[0];
  if (!first) return { email: '', phone: '' };
  return {
    email: first.email ?? '',
    phone: first.telefone ?? '',
  };
}

export const useRegisterPolicyholder = (): UsePolicyholderReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cpfData, setCpfData] = useState<CpfData | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [selectedType, setSelectedType] = useState<'vida' | 'previdencia' | null>(null);
  const [showCnpjSearch, setShowCnpjSearch] = useState(false);
  const [showCpfSearch, setShowCpfSearch] = useState(false);
  const [showCpfInput, setShowCpfInput] = useState(false);
  const [cnpjData, setCnpjData] = useState<CnpjData | null>(null);
  const [searchedCnpj, setSearchedCnpj] = useState<string>('');
  const [pfExistedInCadp, setPfExistedInCadp] = useState(false);
  const [policyListRows, setPolicyListRows] = useState<UserPoliciesListItem[]>([]);
  const [businessLinkRows, setBusinessLinkRows] = useState<BusinessLinkItem[]>([]);
  const [cpfBusinessLinkRows, setCpfBusinessLinkRows] = useState<BusinessLinkItem[]>([]);

  const openForm = () => {
    setShowForm(true);
    resetForm();
  };

  const closeForm = () => {
    setShowForm(false);
    resetForm();
  };

  const selectType = (type: 'vida' | 'previdencia') => {
    setSelectedType(type);
    setShowCnpjSearch(true);
    setShowCpfSearch(false);
    setShowCpfInput(false);
    setCpfData(null);
    setCnpjData(null);
    setPfExistedInCadp(false);
    setPolicyListRows([]);
    setBusinessLinkRows([]);
    setCpfBusinessLinkRows([]);
  };

  const showCnpjSearchField = () => {
    setShowCnpjSearch(true);
  };

  const showCpfSearchField = () => {
    setShowCpfSearch(true);
  };

  const showCpfInputField = () => {
    setShowCpfInput(true);
  };

  const getPolicyholderByCNPJ = async (cnpj: string): Promise<CnpjData | null> => {
    setIsLoading(true);
    setError(null);
    setSearchedCnpj(cnpj);
    const cnpjDigits = documentAlnum(cnpj);

    try {
      console.log('Step 1 (CNPJ): searching legal entity by CNPJ');
      const response = await apiService.policyholder.postGetLegalEntityByCnpj(cnpjDigits);

      if (!response.pessoaJuridica) {
        setError('Documento não encontrado. Não é possível continuar.');
        return null;
      }

      const pj = response.pessoaJuridica;
      const data: CnpjData = {
        id: pj.id,
        versao: pj.versao,
        cnpj: pj.cnpj,
        corporateName: pj.nome,
        fantasyName: pj.nomeFantasia,
        openingDate: pj.dataAberturaJuntaComercial,
        email: '',
        phone: '',
      };

      try {
        console.log('Step 2 (CNPJ): fetching contacts from legal entity');
        const contatosRes = await apiService.policyholder.postContactLookup({
          cpf: '',
          idPessoa: '',
          cnpj: cnpjDigits,
        });
        const list = contatosRes?.contatos ?? [];
        const first = firstContactFromList(list);
        data.email = first.email;
        data.phone = first.phone;
      } catch {
        /* empty list or error: leave fields blank per spec */
      }

      setCnpjData(data);
      setShowCnpjSearch(true);
      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao buscar registro com o documento informado';
      setError(errorMessage);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const getPolicyholderByCPF = async (cpf: string): Promise<CpfData | null> => {
    setIsLoading(true);
    setError(null);
    const cpfDigits = digitsOnly(cpf);
    // Drop previous CPF link status so Situação CPF is not shown for a stale person.
    setCpfBusinessLinkRows([]);

    const empty = (): CpfData => ({
      cpf: cpfDigits,
      name: '',
      email: '',
      phone: '',
      birthDate: '',
      biologicalSex: '',
      nomeMae: '',
      nomePai: '',
      estadoCivilId: '',
      grauEscolaridadeId: '',
    });

    try {
      console.log('Step 1 (CPF): searching person by CPF');
      const response = await apiService.policyholder.postGetPhysicalPersonByCpf(cpfDigits);
      const pf =
        response.pessoaFisicaResponse?.pessoaFisica ?? response.pessoaFisica ?? null;
      const pfId =
        pf?.id ?? (response.idPessoa != null ? String(response.idPessoa) : undefined);

      if (pfId) {
        setPfExistedInCadp(true);
        const base: CpfData = {
          ...empty(),
          idPessoa: pfId,
          versao: pf?.versao,
          cpf: pf?.cpf ?? response.cpf ?? cpfDigits,
          name: pf?.nome ?? '',
          birthDate: pf?.dataNascimento ? pf.dataNascimento.slice(0, 10) : '',
          biologicalSex: pf?.sexo ?? '',
          nomeMae: pf?.nomeMae ?? '',
          estadoCivilId: pf?.estadoCivilId ?? '',
          grauEscolaridadeId: pf?.grauEscolaridadeId ?? '',
        };

        try {
          console.log('Step 2 (CPF): fetching contacts from person');
          const contatosRes = await apiService.policyholder.postContactLookup({
            cpf: cpfDigits,
            idPessoa: '',
            cnpj: '',
          });
          const contatos = contatosRes?.contatos ?? [];
          const validEmails = contatos
            .map((c) => c.email?.trim())
            .filter((email): email is string => isValidEmail(email));
          const firstAvailableEmail = validEmails[0] ?? '';
          // Normal: first non-personal (corporate) domain. Skip: first email in the list.
          const matchedCorporateEmail = SKIP_CORPORATE_EMAIL_STEP
            ? firstAvailableEmail || null
            : pickFirstNonPersonalEmail(validEmails);
          base.email = firstAvailableEmail;
          // Keep corporate email empty when no corporate-domain match is found.
          // This preserves manual input in Step 2 (field stays enabled in the UI).
          base.corporateEmail = matchedCorporateEmail?.trim() ?? '';
          base.hasCorporateEmailFromContacts = Boolean(matchedCorporateEmail?.trim());
          const phoneFromList = contatos.map((c) => c.telefone).find((t) => t != null && t !== '') ?? '';
          base.phone = phoneFromList;
        } catch {
          /* contact fetch is optional */
        }

        setCpfData(base);
        return base;
      }

      setPfExistedInCadp(false);
      const data = empty();
      setCpfData(data);
      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao buscar CPF';
      setError(errorMessage);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const continueAfterCpfStep = async (form: CpfFormSnapshot): Promise<boolean> => {
    if (!cnpjData?.id || !selectedType) {
      setError('Dados da empresa ou ramo ausentes');
      return false;
    }

    const documentoChave = documentoParaChaveLegado(cnpjData.cnpj);
    const cpfDigits = digitsOnly(cpfData?.cpf ?? '');
    if (!documentoChave) {
      setError('Documento da empresa inválido para montar a chave legado (CPF ou CNPJ).');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      let idPfNum: number;

      if (!pfExistedInCadp) {
        const emailDigitado = form.email.trim();
        if (!isValidEmail(emailDigitado)) {
          setError('Informe um e-mail válido.');
          return false;
        }
        if (!SKIP_CORPORATE_EMAIL_STEP && isPersonalEmailDomain(emailDigitado)) {
          setError(
            'Use um e-mail corporativo. Domínios de e-mail pessoal (ex.: Gmail, Hotmail, Outlook) não são permitidos.'
          );
          return false;
        }

        const body: AddBasicPhysicalPersonDataRequest = {
          nome: form.name.trim(),
          telefone: form.phone.trim(),
          nomeDaMae: form.nomeMae.trim(),
          dataNascimento: form.birthDate
            ? new Date(form.birthDate).toISOString().slice(0, 19)
            : '',
          cpf: cpfDigits,
          email: emailDigitado,
          pass: generateRandomPassword8(),
          userName: form.name.trim(),
        };

        console.log('Step 1 (Continuation): registering person');
        const createPfResponse = await apiService.policyholder.postAddBasicPhysicalPersonData(body);
        const newId = createPfResponse?.identificadorPessoa?.idPessoa;
        const nv = createPfResponse?.identificadorPessoa?.numeroVersao;
        if (!newId) {
          setError('Não foi possível cadastrar a pessoa física');
          return false;
        }
        if (!nv) {
          setError('Versão da pessoa física não identificada');
          return false;
        }

        idPfNum = Number(newId);

        if (!SKIP_CORPORATE_EMAIL_STEP) {
          console.log('Step 2 (Continuation): adding contact to person');
          await apiService.policyholder.postAddEmail({
            idPessoa: newId,
            tipoPessoa: TipoPessoaCadp.Fisica,
            origemDado: ORIGEM_DADO_ADICIONAR_EMAIL,
            enderecoEletronico: emailDigitado,
          });
        }

        console.log('Step 3 (Continuation): sending welcome email');
        await apiService.login.sendEmail({
          from: EMAIL_SENDER,
          to: emailDigitado,
          subject: POLICYHOLDER_WELCOME_EMAIL_SUBJECT,
          message: buildPolicyholderWelcomeEmailHtml(undefined, form.name),
        });

        setCpfData((prev) => ({
          ...(prev ?? { cpf: cpfDigits, name: '', email: '', phone: '' }),
          idPessoa: newId,
          versao: nv,
          email: emailDigitado,
          phone: form.phone.trim(),
        }));
      } else {
        idPfNum = Number(cpfData?.idPessoa ?? 0);
        if (!idPfNum) {
          setError('ID da pessoa física ausente');
          return false;
        }
        if (!cpfData?.versao) {
          setError('Versão da pessoa física não identificada');
          return false;
        }

        if (!SKIP_CORPORATE_EMAIL_STEP) {
          const corporate = (form.corporateEmail ?? '').trim();
          if (!isValidEmail(corporate)) {
            setError('Informe um e-mail corporativo válido.');
            return false;
          }
          if (isPersonalEmailDomain(corporate)) {
            setError(
              'Use um e-mail corporativo. Domínios de e-mail pessoal (ex.: Gmail, Hotmail, Outlook) não são permitidos.'
            );
            return false;
          }

          if (!cpfData?.hasCorporateEmailFromContacts) {
            console.log('Step 2 (Continuation): adding corporate email to CADP (person already existed)');
            await apiService.policyholder.postAddEmail({
              idPessoa: cpfData?.idPessoa ?? String(idPfNum),
              tipoPessoa: TipoPessoaCadp.Fisica,
              origemDado: ORIGEM_DADO_ADICIONAR_EMAIL,
              enderecoEletronico: corporate,
            });
            setCpfData((prev) =>
              prev
                ? { ...prev, corporateEmail: corporate, hasCorporateEmailFromContacts: true }
                : prev
            );
          }
        }
      }

      const pjId = Number(cnpjData.id);
      const produtoIds = selectedTypeToProductId(selectedType);

      console.log('Step 4 (Continuation): searching policies and business links');
      const [listRes, vincRes, cpfVincRes] = await Promise.all([
        apiService.policyholder.getUserPoliciesList({
          numDocumentoApol: documentoChave,
        }),
        apiService.policyholder.postListFilteredBusinessLinks({
          idPessoa: pjId,
          produtoIds,
        }),
        apiService.policyholder.postListFilteredBusinessLinks({
          idPessoa: idPfNum,
          produtoIds,
        }),
      ]);
      const rows = listRes.flatMap((g) => g.retorno ?? []);
      setPolicyListRows(rows);
      setBusinessLinkRows(vincRes?.vinculos ?? []);
      setCpfBusinessLinkRows(cpfVincRes?.vinculos ?? []);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao prosseguir o cadastro');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const checkVinculoParaApolice = useCallback(
    async (policy: UserPoliciesListItem): Promise<PolicyVinculoCheckResult | null> => {
      if (!cnpjData?.id || !selectedType || policy.numApoliceApol == null) {
        return null;
      }
      const idPf = cpfData?.idPessoa;
      if (!idPf) return null;
      const documentoChave = documentoParaChaveLegado(cnpjData.cnpj);
      if (!documentoChave) return null;
      const chave = buildEstpChaveLegado(
        documentoChave,
        policy.numApoliceApol,
        selectedTypeToRamoDigit(selectedType)
      );
      const produtoIds = selectedTypeToProductId(selectedType);
      const pjId = Number(cnpjData.id);
      const pfId = Number(idPf);
      console.log('Step 1 (Check): searching business links by legal entity by legacy key');
      const pjPromise = apiService.policyholder.postListFilteredBusinessLinks({
        idPessoa: pjId,
        produtoIds,
        chaveLegado: chave,
      });
      console.log('Step 2 (Check): searching business links by person by legacy key');
      const pfPromise = apiService.policyholder.postListFilteredBusinessLinks({
        idPessoa: pfId,
        produtoIds,
        chaveLegado: chave,
      });
      const [pjRes, pfRes] = await Promise.all([pjPromise, pfPromise]);
      return {
        chaveLegado: chave,
        pjVinculos: pjRes.vinculos ?? [],
        cpfJaVinculadoNestaChave: (pfRes.vinculos ?? []).length > 0,
      };
    },
    [cnpjData, cpfData, selectedType]
  );

  const finalizeRepresentativeLinks = async (
    args: FinalizeRepresentativeArgs,
    responsavelInclusaoId: number
  ): Promise<boolean> => {
    if (!cnpjData?.id || !cnpjData.versao) {
      setError('Dados da pessoa jurídica incompletos');
      return false;
    }
    if (!selectedType) {
      setError('Selecione o ramo (Vida ou Previdência).');
      return false;
    }
    const idPf = cpfData?.idPessoa;
    if (!idPf) {
      setError('ID da pessoa física ausente');
      return false;
    }
    if (!cpfData?.versao) {
      setError('Versão da pessoa física não identificada');
      return false;
    }

    const { existingPairings, newRepresentations } = args;
    if (existingPairings.length === 0 && newRepresentations.length === 0) {
      setError('Associe vínculos às apólices ou selecione apólices para criar representação.');
      return false;
    }

    let idPrim = Number(cnpjData.id);
    let verPrim = Number(cnpjData.versao);
    const idSec = Number(idPf);
    const verSec = Number(cpfData.versao);
    const documentoChave = documentoParaChaveLegado(cnpjData.cnpj);
    if (!documentoChave) {
      setError('Documento da empresa inválido para montar a chave legado (CPF ou CNPJ).');
      return false;
    }
    const ramoDigit = selectedTypeToRamoDigit(selectedType);
    const produtoIds = selectedTypeToProductId(selectedType);

    const assertCpfSemVinculoParaChave = async (policy: UserPoliciesListItem): Promise<boolean> => {
      const numApol = policy.numApoliceApol;
      if (numApol == null) return false;
      const chaveLegado = buildEstpChaveLegado(documentoChave, numApol, ramoDigit);
      console.log('Step 1 (Finalization): validating if person already has a business link for this key');
      const pfRes = await apiService.policyholder.postListFilteredBusinessLinks({
        idPessoa: idSec,
        produtoIds,
        chaveLegado,
      });
      if ((pfRes.vinculos ?? []).length > 0) {
        setError('Este CPF já está vinculado a esta representação de negócio.');
        return false;
      }
      return true;
    };

    const linkOne = async (idRepresentacaoNegocio: number, papelPessoaSecundario: string) => {
      if (!isRepresentativeUserRoleCode(papelPessoaSecundario)) {
        setError('Papel do usuário não identificado');
        return false;
      }

      console.log('Step 2 (Finalization): linking person to business representation');
      await apiService.policyholder.postLinkPersonToBusinessRepresentation({
        idPessoaPrimario: idPrim,
        numeroVersaoPessoaPrimario: verPrim,
        idRepresentacaoNegocio,
        idPessoaSecundario: idSec,
        numeroVersaoPessoaSecundario: verSec,
        papelPessoaSecundario,
        responsavelInclusao: toResponsavelInclusao8(responsavelInclusaoId),
      });
      return true;
    };

    setIsLoading(true);
    setError(null);

    try {
      for (const { policy, papelId } of newRepresentations) {
        if (!isRepresentativeUserRoleCode(papelId)) {
          setError('Papel do usuário não identificado');
          return false;
        }

        const numApol = policy.numApoliceApol;
        if (numApol == null) {
          setError('Apólice sem número (numApoliceApol).');
          return false;
        }
        const chaveLegado = buildEstpChaveLegado(documentoChave, numApol, ramoDigit);
        console.log('Step 3 (Finalization): searching business links by legal entity by legacy key');
        const pjPromise = apiService.policyholder.postListFilteredBusinessLinks({
          idPessoa: idPrim,
          produtoIds,
          chaveLegado,
        });
        const [pjRes, pfOk] = await Promise.all([pjPromise, assertCpfSemVinculoParaChave(policy)]);
        if (!pfOk) return false;
        const pjList = pjRes.vinculos ?? [];
        if (pjList.length > 0) {
          const idRepExisting = pjList[0].representacao?.id;
          if (idRepExisting != null && idRepExisting !== '') {
            if (!(await linkOne(Number(idRepExisting), papelId))) return false;
            console.log('Step 4 (Finalization): updating legal entity version');
            const pjRefresh = await apiService.policyholder.postGetLegalEntityByCnpj(documentoChave);
            const nv = pjRefresh.pessoaJuridica?.versao;
            if (nv != null && nv !== '') {
              verPrim = Number(nv);
              setCnpjData((prev) => (prev ? { ...prev, versao: nv } : prev));
            }
            continue;
          }
        }
        console.log('Step 5 (Finalization): creating business representation');
        const inc = await apiService.policyholder.postIncluirRelacionamentoNegocio({
          id: idPrim,
          versao: verPrim,
          chaveLegado,
          papelId,
          origemDadoId: selectedTypeToOrigemDadoId(selectedType),
          dataInicioVigencia: policyDateToApiIso(policy.dataInicioVigencia, '2000-01-01'),
          dataFimVigencia: policyDateToApiIso(policy.dataFimVigencia, '2099-12-31'),
        });
        const idRepStr = inc.idRepresentacaoNegocio;
        if (idRepStr == null || idRepStr === '') {
          setError('Não foi possível criar a representação de negócio.');
          return false;
        }
        if (!(await linkOne(Number(idRepStr), papelId))) return false;

        console.log('Step 6 (Finalization): updating legal entity version');
        const pjRefresh = await apiService.policyholder.postGetLegalEntityByCnpj(documentoChave);
        const nv = pjRefresh.pessoaJuridica?.versao;
        if (nv != null && nv !== '') {
          verPrim = Number(nv);
          setCnpjData((prev) => (prev ? { ...prev, versao: nv } : prev));
        }
      }

      for (const { vinculo, policy, papelPessoaSecundario } of existingPairings) {
        if (!isRepresentativeUserRoleCode(papelPessoaSecundario)) {
          setError('Papel do usuário não identificado');
          return false;
        }

        const idRepStr = vinculo.representacao?.id;
        if (idRepStr == null || idRepStr === '') {
          setError('Vínculo sem identificador de representação');
          return false;
        }
        const numApol = policy.numApoliceApol;
        if (numApol == null) {
          setError('Apólice sem número (numApoliceApol)');
          return false;
        }
        const built = buildEstpChaveLegado(documentoChave, numApol, ramoDigit);
        const vChave = vinculo.representacao?.chaveLegado;
        if (vChave && vChave !== built) {
          console.log('vChave', vChave);
          console.log('built', built);
          setError('A apólice selecionada não corresponde ao vínculo desta linha.');
          return false;
        }
        if (!(await assertCpfSemVinculoParaChave(policy))) {
          return false;
        }
        if (!(await linkOne(Number(idRepStr), papelPessoaSecundario))) return false;
      }

      console.log('Step 7 (Finalization): refreshing business links for updated CPF status');
      const [vincRes, cpfVincRes] = await Promise.all([
        apiService.policyholder.postListFilteredBusinessLinks({
          idPessoa: idPrim,
          produtoIds,
        }),
        apiService.policyholder.postListFilteredBusinessLinks({
          idPessoa: idSec,
          produtoIds,
        }),
      ]);
      setBusinessLinkRows(vincRes?.vinculos ?? []);
      setCpfBusinessLinkRows(cpfVincRes?.vinculos ?? []);

      setIsSuccess(true);
      return true;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Não foi possível realizar o vínculo. Tente novamente.'
      );
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  const goBack = () => {
    setShowCnpjSearch(true);
    setShowCpfSearch(false);
    setShowCpfInput(false);
    setSearchedCnpj('');
    setCpfData(null);
    setPfExistedInCadp(false);
    setPolicyListRows([]);
    setBusinessLinkRows([]);
    setCpfBusinessLinkRows([]);
  };

  const goToCpfSearch = () => {
    setShowCnpjSearch(false);
    setShowCpfSearch(false);
    setShowCpfInput(true);
    setCpfData(null);
    setPfExistedInCadp(false);
    setPolicyListRows([]);
    setBusinessLinkRows([]);
    setCpfBusinessLinkRows([]);
  };

  const resetForm = () => {
    setSelectedType(null);
    setShowCnpjSearch(false);
    setShowCpfSearch(false);
    setShowCpfInput(false);
    setCpfData(null);
    setCnpjData(null);
    setSearchedCnpj('');
    setError(null);
    setIsSuccess(false);
    setPfExistedInCadp(false);
    setPolicyListRows([]);
    setBusinessLinkRows([]);
    setCpfBusinessLinkRows([]);
  };

  return {
    isLoading,
    error,
    cpfData,
    isSuccess,
    showForm,
    selectedType,
    showCnpjSearch,
    showCpfSearch,
    showCpfInput,
    cnpjData,
    searchedCnpj,
    pfExistedInCadp,
    policyListRows,
    businessLinkRows,
    cpfBusinessLinkRows,
    checkVinculoParaApolice,
    openForm,
    closeForm,
    selectType,
    showCnpjSearchField,
    showCpfSearchField,
    showCpfInputField,
    getPolicyholderByCNPJ,
    getPolicyholderByCPF,
    continueAfterCpfStep,
    finalizeRepresentativeLinks,
    clearError,
    resetForm,
    goBack,
    goToCpfSearch,
  };
};
